import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Pusher from "pusher-js";

import { useAuth } from "../auth";
import {
  SUPPORT_GUEST_CHANGED_EVENT,
  SUPPORT_GUEST_STORAGE_KEY,
  getSupportGuestSession,
  supportMeta,
  supportRealtimeAuth,
  supportSummary,
} from "../../protocols/rest/support";

import { default as SupportContext } from "./context";

const DEFAULT_POLL_SECONDS = 60;

/**
 * Заявки в поддержку: одно на вкладку соединение с realtime (Pusher-совместимый сервер из meta.realtime)
 * и счётчик непрочитанных.
 *
 * - Режим: залогиненный клиент (Bearer) или гость с X-Support-Token; без них ничего не грузим.
 * - Событие realtime несёт только {ticket_id, status, ...} — увеличиваем revision, страницы по нему
 *   перечитывают список / открытую заявку; счётчик перечитываем здесь.
 * - Если realtime выключен или не в состоянии connected — опрос summary раз в poll_seconds,
 *   только когда вкладка видима.
 * - Logout / смена гостевого токена → соединение закрывается, данные сбрасываются.
 */
const SupportProvider = ({ children }) => {
  const { isAuth, user } = useAuth();

  const [guest, setGuest] = useState(() => getSupportGuestSession());
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [revision, setRevision] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);

  const connectedRef = useRef(false);
  const summaryRef = useRef(null);
  const sessionRef = useRef(null);

  const mode = isAuth ? "user" : guest ? "guest" : null;
  const sessionKey = isAuth ? `user:${user?.id ?? user?.email ?? ""}` : guest ? `guest:${guest.token}` : null;

  sessionRef.current = sessionKey;

  // гостевой доступ меняется в protocols/rest/support (verify / 401) и в других вкладках
  useEffect(() => {
    const sync = () => setGuest(getSupportGuestSession());
    const onStorage = event => {
      if (event.key === SUPPORT_GUEST_STORAGE_KEY || event.key === "token") sync();
    };

    window.addEventListener(SUPPORT_GUEST_CHANGED_EVENT, sync);
    window.addEventListener("storage", onStorage);

    return () => {
      window.removeEventListener(SUPPORT_GUEST_CHANGED_EVENT, sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const refreshSummary = useCallback(async ({ bumpOnChange = false } = {}) => {
    const key = sessionRef.current;

    if (!key) return;

    try {
      const data = await supportSummary();

      if (sessionRef.current !== key) return;

      const previous = summaryRef.current;
      summaryRef.current = data;
      setSummary(data);

      // при опросе: что-то поменялось на сервере — открытые страницы перечитают данные
      if (
        bumpOnChange &&
        previous &&
        (previous.last_update !== data?.last_update || previous.unread !== data?.unread || previous.open !== data?.open)
      ) {
        setRevision(value => value + 1);
      }
    } catch (error) {
      console.log({ supportSummaryError: error });
    }
  }, []);

  const reloadMeta = useCallback(async () => {
    const key = sessionRef.current;

    if (!key) return null;

    try {
      const data = await supportMeta();

      if (sessionRef.current === key) setMeta(data);

      return data;
    } catch (error) {
      console.log({ supportMetaError: error });
      return null;
    }
  }, []);

  // смена режима / пользователя / гостевого токена — всё заново
  useEffect(() => {
    setMeta(null);
    setSummary(null);
    setLastEvent(null);
    summaryRef.current = null;
    connectedRef.current = false;

    if (!sessionKey) return;

    reloadMeta();
    refreshSummary();
  }, [sessionKey, reloadMeta, refreshSummary]);

  const realtime = meta?.realtime;
  const realtimeConfigKey = realtime?.enabled && realtime?.key ? JSON.stringify(realtime) : null;

  // realtime: одно соединение, подписка на все каналы из meta
  useEffect(() => {
    if (!sessionKey || !realtimeConfigKey) return;

    const config = JSON.parse(realtimeConfigKey);
    const port = config.port ? Number(config.port) : undefined;

    let pusher;

    try {
      pusher = new Pusher(config.key, {
        wsHost: config.host,
        wsPort: port,
        wssPort: port,
        forceTLS: config.scheme === "https",
        enabledTransports: ["ws", "wss"],
        cluster: config.cluster || "mt1",
        disableStats: true,
        channelAuthorization: {
          customHandler: ({ socketId, channelName }, callback) => {
            supportRealtimeAuth({ socket_id: socketId, channel_name: channelName })
              .then(data => callback(null, data))
              .catch(error => callback(error instanceof Error ? error : new Error("realtime auth failed"), null));
          },
        },
      });
    } catch (error) {
      console.log({ supportRealtimeError: error });
      return;
    }

    pusher.connection.bind("state_change", ({ current }) => {
      const wasConnected = connectedRef.current;
      connectedRef.current = current === "connected";

      // после переподключения могли пропустить события
      if (connectedRef.current && !wasConnected) refreshSummary({ bumpOnChange: true });
    });

    const eventName = config.event || "support.ticket.updated";

    const handleEvent = data => {
      setLastEvent({ ...(data || {}), received_at: Date.now() });
      setRevision(value => value + 1);
      refreshSummary();
    };

    (config.channels || []).forEach(channelName => {
      pusher.subscribe(channelName).bind(eventName, handleEvent);
    });

    return () => {
      connectedRef.current = false;

      try {
        pusher.disconnect();
      } catch (error) {
        console.log({ supportRealtimeError: error });
      }
    };
  }, [sessionKey, realtimeConfigKey, refreshSummary]);

  const pollSeconds = Number(realtime?.poll_seconds) > 0 ? Number(realtime.poll_seconds) : DEFAULT_POLL_SECONDS;

  // запасной опрос: только без живого realtime и только при видимой вкладке
  useEffect(() => {
    if (!sessionKey) return;

    const poll = () => {
      if (document.visibilityState !== "visible" || connectedRef.current) return;

      refreshSummary({ bumpOnChange: true });
    };

    const timer = setInterval(poll, pollSeconds * 1000);

    document.addEventListener("visibilitychange", poll);

    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", poll);
    };
  }, [sessionKey, pollSeconds, refreshSummary]);

  const value = useMemo(
    () => ({
      mode,
      guest,
      meta,
      summary,
      unread: Number(summary?.unread) || 0,
      revision,
      lastEvent,
      refreshSummary,
      reloadMeta,
    }),
    [mode, guest, meta, summary, revision, lastEvent, refreshSummary, reloadMeta]
  );

  return <SupportContext.Provider value={value}>{children}</SupportContext.Provider>;
};

export default SupportProvider;
